// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import cartSummarySharedStyles, { GridItemWithText } from "@keller/App/SharedStyle/Cart/CartSummarySharedStyles";
import { CHECKOUT_STEPS } from "@keller/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@keller/App/Widgets/CheckoutStepper/StepperContext";
import { ButtonPresentationProps } from "@keller/MobiusOverrides/Button";
import { color, mergeWithCss, pluralize } from "@keller/XDL/Utilities";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    // <xng-change> We are only displaying the subtotal on order confirmation page
    showOrderSubtotal?: boolean;
    // Custom message for tax and shipping during cart/checkout process
    showTaxAndShippingInfoMessage?: boolean;
    // <xng-change> flag to show apply promotion
    showApplyPromotion?: boolean;
    showInnerBorders?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPlaceOrderButton: canPlaceOrder(getCurrentCartState(state).value),
    showSubmitForApprovalOrder: canSubmitForApprovalOrder(getCurrentCartState(state).value),
    isOrderConfirmationPage: getCurrentPage(state).type === "OrderConfirmationPage",
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    // XNG-Changes
    titleItem?: GridItemWithText;
    taxAndShippingMessageItem?: GridItemWithText;
    itemsCountItem?: GridItemWithText;
    submitButtonGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    topSeparator?: GridItemProps;
    bottomSeparator?: GridItemProps;
    totalLabelGridItem?: GridItemProps;
    totalValueGridItem?: GridItemProps;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        ...cartSummarySharedStyles.container,
    },
    labelGridItem: {
        ...cartSummarySharedStyles.labelGridItem,
    },
    valueGridItem: {
        ...cartSummarySharedStyles.valueGridItem,
    },
    subtotalLabel: {
        ...cartSummarySharedStyles.label,
    },
    subtotalValue: {
        ...cartSummarySharedStyles.value,
    },
    promotionLabel: {
        ...cartSummarySharedStyles.label,
    },
    promotionValue: {
        ...cartSummarySharedStyles.value,
    },
    discountsLabel: {
        ...cartSummarySharedStyles.label,
    },
    discountsValue: {
        ...cartSummarySharedStyles.value,
    },
    shippingAndHandlingLabel: {
        ...cartSummarySharedStyles.label,
    },
    shippingAndHandlingValue: {
        ...cartSummarySharedStyles.value,
    },
    otherChargesLabel: {
        ...cartSummarySharedStyles.label,
    },
    otherChargesValue: {
        ...cartSummarySharedStyles.value,
    },
    taxLabel: {
        ...cartSummarySharedStyles.label,
    },
    taxValue: {
        ...cartSummarySharedStyles.value,
    },
    totalLabel: {
        ...cartSummarySharedStyles.totalLabel,
    },
    totalValue: {
        ...cartSummarySharedStyles.totalValue,
    },
    loadingSpinner: {
        ...cartSummarySharedStyles.loadingSpinner,
    },
    titleItem: {
        ...cartSummarySharedStyles.titleItem,
    },
    itemsCountItem: {
        ...cartSummarySharedStyles.itemsCountItem,
    },
    taxAndShippingMessageItem: {
        ...cartSummarySharedStyles.taxAndShippingMessageItem,
    },
    submitButtonGridItem: {
        width: 12,
        css: css`
            padding: 11px 17px;
        `,
    },
    submitButton: {
        typographyProps: {
            css: css`
                letter-spacing: 0;
                line-height: 16px;
            `,
        },
        css: css`
            width: 100%;
        `,
    },
    topSeparator: {
        ...cartSummarySharedStyles.topSeparator,
    },
    bottomSeparator: {
        ...cartSummarySharedStyles.bottomSeparator,
    },
    totalLabelGridItem: {
        ...cartSummarySharedStyles.totalLabelGridItem,
    },

    totalValueGridItem: {
        ...cartSummarySharedStyles.totalValueGridItem,
    },
};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    enableVat,
    showOrderSubtotal,
    showTaxAndShippingInfoMessage,
    showApplyPromotion,
    showPlaceOrderButton,
    showSubmitForApprovalOrder,
    extendedStyles,
    showInnerBorders,
    isOrderConfirmationPage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
}: Props) => {
    const [styles] = React.useState(() => mergeWithCss(cartTotalDisplayStyles, extendedStyles));
    const { currentStep } = React.useContext(StepperContext);

    if ((!showPricing && !isCheckoutReviewAndSubmitPage) || isLoading || !cart || isCartEmpty) {
        return null;
    }

    const filteredOrderPromotions = orderPromotions?.filter(o => o.amount > 0);
    const filteredShippingPromotions = shippingPromotions?.filter(o => o.amount > 0);

    return (
        <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`}>
            <GridItem {...styles.titleItem?.container}>
                <Typography {...styles.titleItem?.text}>{translate("Order Summary")}</Typography>
            </GridItem>
            <GridItem {...styles.itemsCountItem?.container}>
                <Typography {...styles.itemsCountItem?.text}>{`${cart.lineCount} ${translate(
                    pluralize(cart.lineCount, "Item"),
                )}`}</Typography>
            </GridItem>
            {showInnerBorders && (
                <GridItem {...styles.topSeparator}>
                    <StyledWrapper />
                </GridItem>
            )}
            {showTaxAndShippingInfoMessage && (
                <GridItem {...styles.taxAndShippingMessageItem?.container}>
                    <Typography {...styles.taxAndShippingMessageItem?.text}>
                        {siteMessage("Keller_Cart_OrderTotalTaxInfoMessage")}
                    </Typography>
                </GridItem>
            )}
            {showApplyPromotion && (
                <GridItem {...styles.taxAndShippingMessageItem?.container}>
                    {createWidgetElement("CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPromotionCode", {
                        fields: {},
                    })}
                </GridItem>
            )}
            {showOrderSubtotal && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                            {cart.orderSubTotalDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {filteredOrderPromotions &&
                filteredOrderPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel} data-test-selector="cartTotal_orderPromotionName">
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.promotionValue} data-test-selector="cartTotal_orderPromotionValue">
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {showTaxAndShipping && cart.shippingAndHandlingDisplay.length > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography
                            {...styles.shippingAndHandlingValue}
                            data-test-selector="cartTotal_shippingAndHandlingAmount"
                        >
                            {isOrderConfirmationPage ? translate("TBD") : cart.shippingAndHandlingDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {filteredShippingPromotions &&
                filteredShippingPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel}>
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography
                                {...styles.promotionValue}
                                data-test-selector="cartTotal_shippingPromotionValue"
                            >
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                            {isOrderConfirmationPage ? translate("TBD") : cart.totalTaxDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {showTaxAndShipping &&
                [...cart.customerOrderTaxes!]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(tax => {
                        const key = `${tax.taxCode}_${tax.sortOrder}`;
                        return (
                            <Fragment key={key}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                </GridItem>
                            </Fragment>
                        );
                    })}
            {showInnerBorders && (
                <GridItem {...styles.bottomSeparator}>
                    <StyledWrapper />
                </GridItem>
            )}
            {/* XNG-Change */}
            <GridItem {...styles.totalLabelGridItem}>
                <Typography {...styles.totalLabel}>{translate("Products Total")}</Typography>
            </GridItem>
            <GridItem {...styles.totalValueGridItem}>
                <Typography
                    {...styles.totalValue}
                    size={cart.orderGrandTotalDisplay.length > 6 ? 19 : styles.totalValue?.size}
                    data-test-selector="cartTotal_orderGrandTotalDisplay"
                >
                    {isOrderConfirmationPage ? translate("TBD") : cart.orderGrandTotalDisplay}
                </Typography>
            </GridItem>

            {discountTotal !== undefined && discountTotal > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.discountsValue} data-test-selector="cartTotal_totalDiscounts">
                            {`${translate("You saved")} `}{" "}
                            <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </>
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showPlaceOrderButton && (
                <GridItem {...styles.submitButtonGridItem}>
                    <PlaceOrderButton styles={styles.submitButton} />
                </GridItem>
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showSubmitForApprovalOrder && (
                <GridItem {...styles.submitButtonGridItem}>
                    <SubmitForApprovalButton extendedStyles={styles.submitButton} />
                </GridItem>
            )}
        </GridContainer>
    );
};

CartTotalDisplay.defaultProps = {
    showInnerBorders: false,
};

export default connect(mapStateToProps)(CartTotalDisplay);
